import { useState } from "react";
import { motion } from "framer-motion";
import { X, Check, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";

const EMPTY = { name: "", email: "", phone: "", cover_note: "", linkedin: "", portfolio: "" };

export function ApplyModal({ job, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: null })); };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email.";
    if (!resume) errs.resume = "Please attach your résumé (PDF/DOC/DOCX).";
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("resume", resume);
      const { data } = await api.post(`/careers/${job.id}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data.id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-transparent border-b border-hairline/20 focus:border-oraya-accent focus:outline-none py-2.5 text-ink placeholder:text-subtle/50 transition-colors text-sm";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="apply-modal" onClick={onClose}>
      <div className="glass-heavy rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-7 py-5 border-b border-hairline/[0.08]">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-subtle">Apply</p>
            <h3 className="display text-lg font-medium text-ink mt-1">{job.title}</h3>
          </div>
          <button onClick={onClose} className="text-subtle hover:text-ink" data-testid="apply-close"><X className="w-5 h-5" /></button>
        </div>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="p-10 text-center" data-testid="apply-success">
            <div className="w-14 h-14 rounded-full bg-invert mx-auto flex items-center justify-center">
              <Check className="w-7 h-7 text-invert-ink" />
            </div>
            <h4 className="display text-2xl font-medium text-ink mt-6">Application received.</h4>
            <p className="text-subtle mt-3">Thank you for applying to ORAYA. We'll be in touch if there's a match.</p>
            <p className="text-subtle/50 text-sm mt-3">Reference: {done}</p>
            <button onClick={onClose} className="mt-6 text-sm text-ink underline underline-offset-4">Close</button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="p-7 flex flex-col gap-4 overflow-y-auto" noValidate>
            <div>
              <input className={field} placeholder="Full name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="apply-name" />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <input className={field} type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <input className={field} placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone" />
            <input className={field} placeholder="LinkedIn URL" value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="apply-linkedin" />
            <input className={field} placeholder="Portfolio / website URL" value={form.portfolio} onChange={(e) => set("portfolio", e.target.value)} data-testid="apply-portfolio" />
            <textarea className={`${field} resize-none`} rows={3} placeholder="Cover note (optional)" value={form.cover_note} onChange={(e) => set("cover_note", e.target.value)} data-testid="apply-cover" />
            <div>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-ink glass-medium rounded-xl px-4 py-3 hover:border-hairline/25 transition-colors">
                <UploadCloud className="w-4 h-4" />
                {resume ? resume.name : "Attach résumé (PDF, DOC, DOCX)"}
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => { setResume(e.target.files?.[0] || null); setErrors((x) => ({ ...x, resume: null })); }} data-testid="apply-resume" />
              </label>
              {errors.resume && <p className="text-xs text-red-500 mt-1">{errors.resume}</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="apply-submit" className="mt-2 rounded-full px-6 py-3 bg-invert text-invert-ink font-medium hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? "Submitting…" : "Submit application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
